import { useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

const DOCUMENT_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/jpeg',
  'image/png',
];

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

const MAX_SIZE = 5 * 1024 * 1024;

interface UseFileValidationOptions {
  fileType?: 'documents' | 'images';
  inputId?: string;
}

export function useFileValidation(options: UseFileValidationOptions = {}) {
  const { fileType = 'documents', inputId = 'file' } = options;
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);

  const allowedTypes = fileType === 'images' ? IMAGE_TYPES : DOCUMENT_TYPES;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.size > MAX_SIZE) {
        toast.error(t('forms.fileTooBig'));
        return;
      }
      if (!allowedTypes.includes(selectedFile.type)) {
        toast.error(t('forms.fileInvalidType'));
        return;
      }
      setFile(selectedFile);
    }
  };

  const removeFile = () => {
    setFile(null);
    const fileInput = document.getElementById(inputId) as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const validateRequired = (): boolean => {
    if (!file) {
      toast.error(t('forms.fileRequired'));
      return false;
    }
    return true;
  };

  return { file, handleFileChange, removeFile, validateRequired };
}
